/**
 * SLR Rotation Tool - React Frontend
 * 
 * Features:
 * - Full rotation schedule UI
 * - Google Calendar sync
 * - PTO import from calendar
 * - Local persistence via backend API
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';

import {
  DEFAULT_BLOCKS_PER_WEEK,
  DEFAULT_ZONES,
  formatZoneLocalWindow,
  addPtoRange,
  clearOverrides,
  clrRotationFromWeeks,
  firstMondayOnOrAfter,
  formatDate,
  generateSchedule as buildSchedule,
  getMonday,
  listMembers,
  normalizeRoster,
  parseDate,
  shiftDate,
  setOverrideRange,
  getBlockLabels,
  getScheduleRange,
  sortZones,
  validateZones,
  zoneColor,
  zoneEnd,
} from './schedule.mjs';
import { api } from './api';
import { btn, card, field, table, text } from './ui';
import AppHeader from './components/AppHeader';
import LoadingScreen from './components/LoadingScreen';
import LoginScreen from './components/LoginScreen';
import Notice from './components/Notice';
import TabBar from './components/TabBar';
import ConfirmDialog from './components/ConfirmDialog';
import RotationFrequency from './components/RotationFrequency';
import SlrRosterPanel from './components/SlrRosterPanel';
import ZonesPanel from './components/ZonesPanel';
import PtoPanel from './components/PtoPanel';
import SettingsTab from './components/SettingsTab';
import SwapPanel from './components/SwapPanel';
import ClrEditor from './components/ClrEditor';

// Default values - will be loaded from backend/config
const defaultRoster = null;
const defaultClrRoster = [];


export default function App() {
  // State
  const [activeTab, setActiveTab] = useState('schedule');
  const [scheduleStart, setScheduleStart] = useState('2026-01-01');
  const [weeksToShow, setWeeksToShow] = useState(12);
  const [roster, setRoster] = useState(defaultRoster);
  const [ptoEntries, setPtoEntries] = useState([]);
  const [overrides, setOverrides] = useState({});

  // Zone configuration (shift windows are UTC; local labels are derived)
  const [zones, setZones] = useState(DEFAULT_ZONES);
  const [blocksPerWeek, setBlocksPerWeek] = useState(DEFAULT_BLOCKS_PER_WEEK);
  const [editingZones, setEditingZones] = useState(false);
  const [zonesDraft, setZonesDraft] = useState([]);
  // Blocks per week is only editable while editing zones or simulating, and in
  // both cases it stays a draft. null means "same as live".
  const [blocksDraft, setBlocksDraft] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [simZones, setSimZones] = useState(null);
  
  // CLR state (membership and order come from the Settings team table)
  const [clrRoster, setClrRoster] = useState(defaultClrRoster);
  const [clrStartIndex, setClrStartIndex] = useState(0); // Starting position in rotation
  const [clrAnchorDate, setClrAnchorDate] = useState(null); // Absolute Monday for rotation week 0
  
  // Auth & Calendar state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [ptoCalendarIds, setPtoCalendarIds] = useState([]); // Changed to array for multiple calendars
  const [targetCalendarId, setTargetCalendarId] = useState(''); // Single target for both SLR and CLR
  const [emailToName, setEmailToName] = useState({});
  const [syncStatus, setSyncStatus] = useState('');
  // Inline dismissible banner: { type: 'success' | 'error' | 'info', text, lines }
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  
  // Form state
  const [newPtoName, setNewPtoName] = useState('');
  const [newPtoStart, setNewPtoStart] = useState('');
  const [newPtoEnd, setNewPtoEnd] = useState('');
  const [confirmSync, setConfirmSync] = useState(false);
  const [rebuildAll, setRebuildAll] = useState(false);
  const [confirmClr, setConfirmClr] = useState(null);

  // Success and info banners clear themselves; errors stay until dismissed.
  const showNotice = useCallback((type, text, lines = []) => {
    setNotice({ type, text, lines });
    if (type !== 'error') {
      clearTimeout(showNotice.timer);
      showNotice.timer = setTimeout(() => setNotice(null), 8000);
    }
  }, []);

  // Zones are ordered follow-the-sun by their UTC start.
  const orderedZones = useMemo(() => sortZones(zones), [zones]);
  const zoneNames = useMemo(() => orderedZones.map((z) => z.name), [orderedZones]);
  const blockLabels = useMemo(() => getBlockLabels(blocksPerWeek), [blocksPerWeek]);
  const blocksPreview = blocksDraft ?? blocksPerWeek;
  const previewLabels = useMemo(() => getBlockLabels(blocksPreview), [blocksPreview]);
  const activeDraft = editingZones ? zonesDraft : simZones || [];
  const setActiveDraft = editingZones ? setZonesDraft : setSimZones;
  const zoneErrors = useMemo(
    () => validateZones(editingZones || simulating ? activeDraft : zones).errors,
    [editingZones, simulating, activeDraft, zones]
  );

  // A simulation reads the projection for zones the schedule is not running.
  const projectionZones = useMemo(
    () => (simulating ? sortZones(simZones || []).map((z) => z.name).filter(Boolean) : zoneNames),
    [simulating, simZones, zoneNames]
  );
  const previewNote = simulating
    ? 'simulation, nothing saved'
    : blocksPreview !== blocksPerWeek
      ? 'unsaved, not applied yet'
      : null;

  // Load data on mount
  useEffect(() => {
    async function loadAll() {
      try {
        const { data: authData } = await api.authStatus();
        setIsAuthenticated(authData.authenticated);
        setUser(authData.user);
        
        if (authData.authenticated) {
          const { ok: dataOk, data } = await api.loadAll();
          if (dataOk) {
            if (data.roster) setRoster(normalizeRoster(data.roster));
            if (data.pto) setPtoEntries(data.pto);
            if (data.overrides) setOverrides(data.overrides);
            if (data.clr?.roster?.length > 0) setClrRoster(data.clr.roster);
            if (data.clr?.startIndex !== undefined) setClrStartIndex(data.clr.startIndex);
            if (data.clr?.anchorDate) setClrAnchorDate(data.clr.anchorDate);
            if (data.settings?.scheduleStart) setScheduleStart(data.settings.scheduleStart);
            if (data.settings?.weeksToShow) setWeeksToShow(data.settings.weeksToShow);
            if (data.settings?.zones?.length) setZones(data.settings.zones);
            if (data.settings?.blocksPerWeek) setBlocksPerWeek(data.settings.blocksPerWeek);
          }
          
          const { ok: configOk, data: config } = await api.calendarConfig();
          if (configOk) {
            setPtoCalendarIds(config.ptoCalendarIds || []);
            setTargetCalendarId(config.targetCalendarId || '');
            setEmailToName(config.emailToName || {});
          }
        }
        
        // Load calendars if authenticated
        if (authData.authenticated) {
          try {
            const { ok, data: calData } = await api.listCalendars();
            if (ok) {
              setCalendars(calData.calendars || []);
            } else {
              console.error('Failed to load calendars:', calData);
            }
          } catch (err) {
            console.error('Error fetching calendars:', err);
          }
        }
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadAll();
    
    // Listen for auth callback
    const handleMessage = async (event) => {
      if (event.data?.type === 'AUTH_SUCCESS') {
        // Try to verify session in main window context
        try {
          const { data: verifyData } = await api.verifySession();
          
          if (verifyData.authenticated) {
            setIsAuthenticated(true);
            setUser(verifyData.user);
            // Reload to fetch all data
            window.location.reload();
          } else {
            console.error('Session verification failed');
          }
        } catch (err) {
          console.error('Failed to verify session:', err);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Save data to backend
  const saveRosterToBackend = useCallback((newRoster) => api.saveRoster(newRoster), []);
  const savePtoToBackend = useCallback((newPto) => api.savePto(newPto), []);
  const saveOverridesToBackend = useCallback((newOverrides) => api.saveOverrides(newOverrides), []);
  const saveClrToBackend = useCallback(
    (roster, startIndex, anchorDate) => api.saveClr(roster, startIndex, anchorDate),
    [],
  );
  const saveSettingsToBackend = useCallback((settings) => api.saveSettings(settings), []);

  // Generate schedule with CLR
  const generateSchedule = useCallback(
    () =>
      buildSchedule({
        scheduleStart,
        weeksToShow,
        roster,
        zones,
        blocksPerWeek,
        ptoEntries,
        overrides,
        clrRoster,
        clrStartIndex,
        clrAnchorDate,
      }),
    [scheduleStart, weeksToShow, roster, zones, blocksPerWeek, ptoEntries, overrides, clrRoster, clrStartIndex, clrAnchorDate]
  );

  // Generate flat schedule for calendar sync
  const getFlatSchedule = useCallback(() => {
    const entries = [];
    const schedule = generateSchedule(); // Call the function to get the schedule
    schedule.forEach(week => {
      week.days.forEach(day => {
        Object.keys(day.people).forEach(tz => {
          entries.push({
            date: day.date,
            person: day.people[tz],
            tz,
            isWeekend: day.isWeekend,
            hasPto: day.hasPto[tz] || false,
          });
        });
      });
    });
    return entries;
  }, [generateSchedule]);

  // Auth handlers
  const handleLogin = async () => {
    try {
      const { data } = await api.authUrl();
      
      // Open Google Sign-In in popup
      const authWindow = window.open(data.authUrl, 'Google Sign-In', 'width=500,height=600');
      
      // Listen for auth success message
      const checkAuth = setInterval(async () => {
        if (authWindow.closed) {
          clearInterval(checkAuth);
          // Check auth status
          const { data: authData } = await api.authStatus();
          if (authData.authenticated) {
            setIsAuthenticated(true);
            setUser(authData.user);
            window.location.reload(); // Reload to fetch data
          }
        }
      }, 1000);
    } catch (err) {
      setAuthError('Failed to initiate login: ' + err.message);
    }
  };

  const handleLogout = async () => {
    await api.logout();
    setIsAuthenticated(false);
    setUser(null);
    setCalendars([]);
  };

  // Import PTO from multiple calendars
  const importPtoFromCalendar = async () => {
    if (!ptoCalendarIds || ptoCalendarIds.length === 0) {
      alert('Please select at least one PTO calendar first');
      return;
    }
    
    setSyncStatus('Importing PTO from multiple calendars...');
    try {
      const startDate = scheduleStart;
      const endDate = shiftDate(scheduleStart, weeksToShow * 7);
      
      let allPtoEntries = [];
      
      // Fetch PTO from all selected calendars
      for (const calendarId of ptoCalendarIds) {
        const { data } = await api.importPto({
          startDate,
          endDate,
          calendarId,
        });
        
        if (!data.error && data.pto) {
          allPtoEntries = [...allPtoEntries, ...data.pto];
        }
      }
      
      // Merge with existing PTO (avoid duplicates)
      const existingKeys = new Set(ptoEntries.map(p => `${p.name}-${p.date}`));
      const newEntries = allPtoEntries.filter(p => !existingKeys.has(`${p.name}-${p.date}`));
      const merged = [...ptoEntries, ...newEntries];
      
      setPtoEntries(merged);
      savePtoToBackend(merged);
      setSyncStatus(`Imported ${newEntries.length} new PTO entries from ${ptoCalendarIds.length} calendars`);
    } catch (err) {
      setSyncStatus(`Error: ${err.message}`);
    }
  };

  // Read the CLR: events off the calendar and preview the rebuilt rotation
  // before saving it, so a wrong order is caught before it is applied.
  const rebuildClrFromCalendar = async () => {
    setSyncStatus('Reading CLR events from the calendar...');
    const { ok, data } = await api.getCalendarClr(targetCalendarId);
    setSyncStatus('');
    if (!ok || !data || data.error) {
      showNotice('error', `Could not read the calendar: ${data?.error || 'request failed'}`);
      return;
    }
    const { roster, startIndex, warnings } = clrRotationFromWeeks(data.weeks || [], scheduleStart, clrRoster);
    if (!roster.length) {
      showNotice('error', 'No CLR events found on the calendar.');
      return;
    }
    const firstMonday = formatDate(firstMondayOnOrAfter(scheduleStart));
    const n = roster.length;
    const lines = [];
    for (let k = 0; k < Math.min(4, n); k++) {
      lines.push(`${shiftDate(firstMonday, k * 7)}: ${roster[(startIndex + k) % n]}`);
    }
    lines.push(`Order (${n}): ${roster.join(' → ')}`);
    warnings.forEach((w) => lines.push(`⚠ ${w}`));
    setConfirmClr({ roster, startIndex, lines });
  };

  const applyClrRebuild = async () => {
    if (!confirmClr) return;
    const { roster, startIndex } = confirmClr;
    setClrRoster(roster);
    setClrStartIndex(startIndex);
    setClrAnchorDate(null); // legacy startIndex path; clear any absolute anchor
    const { ok } = await api.saveClr(roster, startIndex, null);
    setConfirmClr(null);
    showNotice(ok ? 'success' : 'error', ok ? 'CLR rebuilt from the calendar.' : 'Failed to save the CLR.');
  };

  // Save a hand-set CLR order, anchoring week 0 to this Monday (absolute), so the
  // top of the list is on call this week regardless of the schedule's Start date.
  const thisWeekMonday = formatDate(getMonday(parseDate(new Date().toISOString().slice(0, 10))));
  const saveClrOrder = async (order) => {
    setClrRoster(order);
    setClrStartIndex(0);
    setClrAnchorDate(thisWeekMonday);
    const { ok } = await saveClrToBackend(order, 0, thisWeekMonday);
    showNotice(ok ? 'success' : 'error', ok ? 'CLR rotation saved.' : 'Failed to save the rotation.');
  };

  // Save a hand-set SLR order per zone. The order is week 1; the rotation math
  // is unchanged, so fairness holds and later team edits keep this order.
  const saveSlrOrder = async (nextRoster) => {
    setRoster(nextRoster);
    const { ok } = await saveRosterToBackend(nextRoster);
    showNotice(ok ? 'success' : 'error', ok ? 'SLR order saved.' : 'Failed to save the order.');
  };

  // Sync both SLR and CLR schedules to the same calendar
  const syncToCalendar = async () => {
    setConfirmSync(false);
    if (!targetCalendarId) {
      showNotice('error', 'Select a target calendar under Settings first');
      return;
    }

    setSyncStatus('Syncing SLR and CLR to calendar...');
    try {
      // Get SLR schedule
      const slrSchedule = getFlatSchedule();
      
      // Get CLR schedule (add CLR info to each week)
      const fullSchedule = generateSchedule();
      const clrEvents = [];
      
      fullSchedule.forEach(week => {
        if (week.clr && week.clr !== '—') {
          // Create CLR event for the week (Monday to Sunday)
          const monday = week.startDate;
          const sunday = shiftDate(week.startDate, 6);
          
          clrEvents.push({
            type: 'CLR',
            person: week.clr,
            startDate: monday,
            endDate: sunday,
            isClr: true
          });
        }
      });
      
      const { ok, status, data } = await api.syncCalendar({
        slrSchedule,
        clrSchedule: clrEvents,
        calendarId: targetCalendarId,
        clearExisting: rebuildAll,
      });

      if (!ok || data?.error) {
        throw new Error(data?.error || (status === 409 ? 'A sync is already running' : 'Sync failed to start'));
      }

      // The sync runs in the background (it is too slow for the gateway timeout),
      // so poll its status until it finishes.
      setSyncStatus('Syncing to calendar in the background...');
      for (;;) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: s } = await api.syncStatus();
        if (!s) continue;
        if (s.running) {
          setSyncStatus(
            s.phase === 'listing'
              ? 'Reading the calendar...'
              : `Updating the calendar ${s.done}/${s.total}...`
          );
          continue;
        }
        if (s.phase === 'error') {
          setSyncStatus(`Error: ${s.error || 'sync failed'}`);
        } else {
          setSyncStatus(`Synced: ${s.created} created, ${s.updated} updated, ${s.deleted} deleted, ${s.unchanged} unchanged${s.errors ? `, ${s.errors} errors` : ''}`);
        }
        break;
      }
    } catch (err) {
      setSyncStatus(`Error: ${err.message}`);
    }
  };

  // The team table owns the zone assignments, the CLR rotation and the email
  // map, so saving it writes all three.
  const saveTeam = async (map, nextRoster, nextClr) => {
    setEmailToName(map);
    setRoster(nextRoster);
    setClrRoster(nextClr);
    await Promise.all([
      saveRosterToBackend(nextRoster),
      saveClrToBackend(nextClr, clrStartIndex, clrAnchorDate),
      api.saveCalendarConfig({ ptoCalendarIds, targetCalendarId, emailToName: map }),
    ]);
    showNotice('success', 'Team saved', [
      'Google Calendar is not changed until you run Sync to Calendar.',
    ]);
  };

  // Save calendar config
  const saveCalendarConfig = async () => {
    await api.saveCalendarConfig({ ptoCalendarIds, targetCalendarId, emailToName });
    setSyncStatus('Calendar config saved');
  };

  // Throw away unsaved edits by reloading what is stored.
  const reloadCalendarConfig = async () => {
    const { ok, data } = await api.calendarConfig();
    if (!ok) {
      showNotice('error', 'Could not reload the saved settings');
      return;
    }
    setPtoCalendarIds(data.ptoCalendarIds || []);
    setTargetCalendarId(data.targetCalendarId || '');
    setEmailToName(data.emailToName || {});
    setSyncStatus('');
    showNotice('info', 'Reverted to the saved settings');
  };

  // Roster handlers
  // Zone handlers
  const handleZonesEdit = () => {
    setZonesDraft(orderedZones.map((z) => ({ ...z })));
    setEditingZones(true);
  };

  const updateZoneDraft = (index, patch) => {
    setActiveDraft(activeDraft.map((zone, i) => (i === index ? { ...zone, ...patch } : zone)));
  };

  // Persists zones and keeps the roster in step: a removed zone takes its
  // people with it, and a new zone starts empty.
  const applyZones = (nextZones, nextBlocks) => {
    setZones(nextZones);
    setBlocksPerWeek(nextBlocks);
    saveSettingsToBackend({ zones: nextZones, blocksPerWeek: nextBlocks });

    // Only touch the roster when the zones themselves changed, and never
    // before it has loaded, otherwise a blocks-only change would blank it.
    if (!roster) return;
    const names = nextZones.map((z) => z.name);
    const dropped = Object.keys(roster).filter((name) => !names.includes(name));
    const added = names.filter((name) => !(name in roster));
    if (dropped.length === 0 && added.length === 0) return;

    const nextRoster = Object.fromEntries(names.map((name) => [name, roster[name] || []]));
    setRoster(nextRoster);
    saveRosterToBackend(nextRoster);

    if (dropped.length > 0) {
      setOverrides((current) => {
        const kept = Object.fromEntries(
          Object.entries(current).filter(([key]) => !dropped.some((name) => key.endsWith(`-${name}`)))
        );
        saveOverridesToBackend(kept);
        return kept;
      });
    }
  };

  const saveZones = () => {
    const cleaned = zonesDraft.map((z) => ({ ...z, name: String(z.name).trim() }));
    const { ok, errors } = validateZones(cleaned);
    if (!ok) {
      showNotice('error', 'Zones not saved. Fix these first:', errors);
      return;
    }
    applyZones(sortZones(cleaned), blocksPreview);
    setEditingZones(false);
    setBlocksDraft(null);
    showNotice(
      'success',
      `Zones saved: ${cleaned.length} zone${cleaned.length === 1 ? '' : 's'} covering 24h, ` +
        `${blocksPreview} block${blocksPreview === 1 ? '' : 's'} per week ` +
        `(${getBlockLabels(blocksPreview).join(', ')}).`,
      ['Google Calendar is not changed until you run Sync to Calendar in Settings.']
    );
  };



  // PTO handlers
  const addPTO = (name, startDate, endDate) => {
    const merged = addPtoRange(ptoEntries, name, startDate, endDate);
    if (merged === ptoEntries) return;
    setPtoEntries(merged);
    savePtoToBackend(merged);
  };

  // Swap handlers. A swap covers a date range, stored one key per day for a
  // zone and one per week for CLR, so removing takes the whole group.
  const applySwap = (swap) => {
    const next = setOverrideRange(overrides, swap);
    setOverrides(next);
    saveOverridesToBackend(next);
  };

  const removeSwap = (keys) => {
    const next = clearOverrides(overrides, keys);
    setOverrides(next);
    saveOverridesToBackend(next);
  };

  const allMembers = listMembers(roster, zoneNames);

  // Names the app has seen anywhere, offered when assigning people to zones.
  const knownPeople = useMemo(
    () => [...new Set([...allMembers, ...clrRoster, ...Object.values(emailToName)])].sort(),
    [allMembers, clrRoster, emailToName]
  );

  const schedule = useMemo(() => generateSchedule(), [generateSchedule]);

  const tabs = [
    { id: 'schedule', label: 'Schedule' },
    { id: 'roster', label: 'Roster' },
    { id: 'pto', label: 'PTO' },
    { id: 'settings', label: '⚙️ Settings' },
  ];

  if (loading) return <LoadingScreen />;

  if (!isAuthenticated) return <LoginScreen authError={authError} onLogin={handleLogin} />;

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <AppHeader user={user} onLogout={handleLogout} />

      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onSelect={setActiveTab}
        showActions={isAuthenticated}
        onImportPto={importPtoFromCalendar}
        importDisabled={ptoCalendarIds.length === 0}
        onRebuildClr={rebuildClrFromCalendar}
        rebuildDisabled={!targetCalendarId}
        onSync={() => { setRebuildAll(false); setConfirmSync(true); }}
        syncDisabled={!targetCalendarId}
      />

      <ConfirmDialog
        open={confirmSync}
        title="Sync the schedule to Google Calendar?"
        lines={[
          rebuildAll
            ? `Deletes and recreates the SLR and CLR events for ${schedule.length} weeks from ${scheduleStart}.`
            : `Writes only the SLR and CLR events that changed, for ${schedule.length} weeks from ${scheduleStart}.`,
          'Events that ended before today (UTC) and events outside these weeks are left as they are.',
          'Swaps and PTO shown here are included; an SLR or CLR event edited directly in Calendar is reset to match.',
        ]}
        confirmLabel={rebuildAll ? 'Rebuild' : 'Sync'}
        onConfirm={syncToCalendar}
        onCancel={() => setConfirmSync(false)}
      >
        <label className="flex items-center gap-2 text-sm text-gray-700 mb-4 cursor-pointer">
          <input type="checkbox" checked={rebuildAll} onChange={(e) => setRebuildAll(e.target.checked)} />
          Rebuild all events in these weeks (delete and recreate)
        </label>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!confirmClr}
        title="Rebuild CLR from the calendar?"
        lines={confirmClr?.lines || []}
        confirmLabel="Apply"
        onConfirm={applyClrRebuild}
        onCancel={() => setConfirmClr(null)}
      />

      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      {syncStatus && (() => {
        const isError = /^error/i.test(syncStatus);
        const isDone = /^(synced|imported)/i.test(syncStatus);
        const running = !isError && !isDone;
        const tone = isError ? 'bg-red-600' : isDone ? 'bg-green-600' : 'bg-blue-600';
        return (
          <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${tone}`}>
            {running && (
              <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            )}
            <span>{syncStatus}</span>
            {!running && (
              <button onClick={() => setSyncStatus('')} className="ml-1 text-white/80 hover:text-white" aria-label="Dismiss">✕</button>
            )}
          </div>
        );
      })()}


      {/* Schedule Tab */}
      {activeTab === 'schedule' && (
        <div>
          <div className="flex gap-4 mb-4 items-center flex-wrap">
            <label className="text-sm">
              Start:
              <input
                type="date"
                value={scheduleStart}
                onChange={(e) => {
                  setScheduleStart(e.target.value);
                  saveSettingsToBackend({ scheduleStart: e.target.value, weeksToShow });
                }}
                className={field.inputInline}
              />
            </label>
            <label className="text-sm">
              Weeks:
              <select
                value={weeksToShow}
                onChange={(e) => {
                  const w = Number(e.target.value);
                  setWeeksToShow(w);
                  saveSettingsToBackend({ scheduleStart, weeksToShow: w });
                }}
                className={field.inputInline}
              >
                {[4, 8, 12, 16, 24].map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </label>
          </div>

          <div className="space-y-4">
            {schedule.map((week, wIdx) => (
              <div key={wIdx} className="border rounded-lg overflow-hidden border-gray-300">
                <div className="px-4 py-2 font-medium flex justify-between items-center bg-gray-100">
                  <span>Week {week.weekNum} — Starting {week.startDate}</span>
                  <span className="bg-white px-3 py-1 rounded-full text-sm font-semibold shadow-sm">
                    👤 CLR: {week.clr || '—'}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border-t p-2 text-left w-32">Shift</th>
                        {week.days.map((day, dIdx) => (
                          <th key={dIdx} className={`border-t p-2 text-center ${day.isWeekend ? 'bg-yellow-50 font-bold' : ''}`}>
                            {day.dayName}<br/>
                            <span className="text-xs font-normal text-gray-500">{day.date.slice(5)}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {orderedZones.map((zone, zIdx) => {
                        const tz = zone.name;
                        return (
                        <tr key={tz}>
                          <td className="border-t p-2">
                            <span className={`px-2 py-1 rounded text-xs ${zoneColor(zIdx)}`}>{tz}</span>
                            <div className={text.hintTight}>
                              {formatZoneLocalWindow(zone, week.startDate) || `${zone.start}-${zoneEnd(zone)} UTC`}
                            </div>
                          </td>
                          {week.days.map((day, dIdx) => (
                            <td key={dIdx} className={`border-t p-2 text-center ${day.isWeekend ? 'bg-yellow-50' : ''} ${day.hasPto[tz] ? 'bg-red-100' : ''} ${day.hasOverride[tz] ? 'bg-blue-50' : ''}`}>
                              <span className={day.isWeekend ? 'font-medium' : ''}>
                                {day.hasOverride[tz] ? (
                                  <span>
                                    <span className="line-through text-red-500 text-xs">{day.basePerson[tz]}</span>
                                    {' '}
                                    <span className="font-medium">{day.people[tz]}</span>
                                  </span>
                                ) : (
                                  day.people[tz]
                                )}
                              </span>
                              {day.hasPto[tz] && <span className="text-xs text-red-600 ml-1">⚠</span>}
                            </td>
                          ))}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 p-3 bg-gray-50 rounded text-sm">
            <p className="font-medium mb-2">Legend:</p>
            <div className="flex gap-4 flex-wrap text-xs">
              <span><span className="inline-block w-4 h-4 bg-yellow-50 border border-yellow-300 mr-1 align-middle"></span> Weekend</span>
              <span><span className="inline-block w-4 h-4 bg-red-100 border border-red-300 mr-1 align-middle"></span> PTO conflict ⚠</span>
              <span><span className="inline-block w-4 h-4 bg-blue-50 border border-blue-300 mr-1 align-middle"></span> Swap: <span className="line-through text-red-500">Original</span> <strong>Replacement</strong></span>
            </div>
          </div>
        </div>
      )}

      {/* Roster Tab */}
      {activeTab === 'roster' && (
        <div>
          <ZonesPanel
            zones={zones}
            orderedZones={orderedZones}
            blocksPerWeek={blocksPerWeek}
            blockLabels={blockLabels}
            zoneErrors={zoneErrors}
            roster={roster}
            scheduleStart={scheduleStart}
            editing={editingZones}
            draft={activeDraft}
            setDraft={setActiveDraft}
            onEdit={handleZonesEdit}
            onCancel={() => {
              setEditingZones(false);
              setBlocksDraft(null);
            }}
            simulating={simulating}
            onSimulate={() => {
              setSimZones(orderedZones.map((z) => ({ ...z })));
              setSimulating(true);
            }}
            onSimulateExit={() => {
              setSimulating(false);
              setSimZones(null);
              setBlocksDraft(null);
            }}
            onSave={saveZones}
            onUpdateDraft={updateZoneDraft}
            blocksPreview={blocksPreview}
            onBlocksPreview={(next) => setBlocksDraft(next)}
          />

          <RotationFrequency
            zoneNames={projectionZones}
            roster={roster}
            clrRoster={clrRoster}
            blockLabels={previewLabels}
            blocksPerWeek={blocksPreview}
            previewNote={previewNote}
          />

          <SlrRosterPanel
            orderedZones={orderedZones}
            roster={roster}
            scheduleStart={scheduleStart}
            onSave={saveSlrOrder}
          />

          <ClrEditor
            clrRoster={clrRoster}
            thisWeek={thisWeekMonday}
            onSave={saveClrOrder}
          />

          <SwapPanel
            zoneNames={zoneNames}
            schedule={schedule}
            people={knownPeople}
            overrides={overrides}
            onApply={applySwap}
            onRemove={removeSwap}
            scheduleRange={getScheduleRange(schedule)}
          />

        </div>
      )}

      {/* PTO Tab */}
      {activeTab === 'pto' && (
        <div className="max-w-2xl">
          <PtoPanel
            ptoEntries={ptoEntries}
            setPtoEntries={setPtoEntries}
            savePtoToBackend={savePtoToBackend}
            allMembers={allMembers}
            newPtoName={newPtoName}
            setNewPtoName={setNewPtoName}
            newPtoStart={newPtoStart}
            setNewPtoStart={setNewPtoStart}
            newPtoEnd={newPtoEnd}
            setNewPtoEnd={setNewPtoEnd}
            addPTO={addPTO}
          />
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <SettingsTab
          isAuthenticated={isAuthenticated}
          onLogin={handleLogin}
          calendars={calendars}
          setCalendars={setCalendars}
          ptoCalendarIds={ptoCalendarIds}
          setPtoCalendarIds={setPtoCalendarIds}
          targetCalendarId={targetCalendarId}
          setTargetCalendarId={setTargetCalendarId}
          emailToName={emailToName}
          onSave={saveCalendarConfig}
          onCancel={reloadCalendarConfig}
          onSaveTeam={saveTeam}
          roster={roster}
          zoneNames={zoneNames}
          clrRoster={clrRoster}
          syncStatus={syncStatus}
          showNotice={showNotice}
        />
      )}
    </div>
  );
}